import { ClickAwayListener, Modal, Box, Popover } from "@mui/material";
import React, { useContext, useState } from "react";
import {
  LocalizationProvider,
  PickersLayoutRoot,
  StaticDatePicker,
  usePickerLayout,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ur";
import "dayjs/locale/sd";
import styles from "./styles.module.css";
import ParagraphText from "../ParagraphText";
import dayjs from "dayjs";
import moment from "moment";
import { CalendarIconNew, CalenderIcon } from "./../../assets/icons/icon";
import UBButton from "../UBButton";
import { LanguageContext } from "../../context/LanguageContext";
import { analytics, triggerLogEvent } from "../../config/firebase";

const CustomLayout = (props) => {
  const { toolbar, content, shortcuts } = usePickerLayout(props);
  return (
    <PickersLayoutRoot ownerState={props} className="PickersLayoutRoot">
      <div className="shortcuts">{shortcuts}</div>
      <div className="contentWrapper">
        <div className="content">{content}</div>
      </div>
    </PickersLayoutRoot>
  );
};

const UBDateSelect = ({
  selectedDate,
  setSelectedDate,
  title,
  className = "",
  isRequired = false,
  disablePast = false,
  disableFuture = false,
  isModal = false,
  closeOnDateSelect = false,
  minDate,
  maxDate,
  placeholder,
  placeholderColor,
  isCalendericon = false,
  handleLogEvent = () => {},
  id = "",
  error = "",
  disabled = false,
  onClick = null,
}) => {
  const [I18n] = useContext(LanguageContext);
  const resolvedTitle = title === undefined ? (I18n.date || "Date") : title;
  const DAYJS_LOCALE_MAP = { ur: "ur", snd: "sd" };
  const dayjsLocale = DAYJS_LOCALE_MAP[localStorage.getItem("language")] || "en";
  const [toggle, setToggle] = useState(false);
  const [date, setDate] = useState();
  const [openModal, setOpenModal] = useState(false);
  const anchorRef = React.useRef(null);

  const handleClickAway = () => {
    if (!isModal) {
      setToggle(false);
    }
  };

  const handleToggle = () => {
    if (disabled) {
      onClick?.();
      return;
    }
    if (!toggle) {
      handleLogEvent();
      setDate(undefined);
    }
    setToggle((prev) => !prev);
  };

  const handleChange = (date) => {
    setDate(date);
    if (closeOnDateSelect && date) {
      const dateObj = date?.toDate ? date.toDate() : new Date(date);
      dateObj.setHours(0, 0, 0, 0);
      setSelectedDate(dateObj);
      setToggle(false);
    }
  };

  const onClearDate = () => {
    setSelectedDate("");
    if (isModal) handleCloseModal();
    else handleToggle();
  };

  const onApplyDate = () => {
    setSelectedDate(date);
    if (isModal) handleCloseModal();
    else handleToggle();
  };

  // moment (not dayjs) so the Urdu right-to-left date fix in LanguageContext applies.
  // Picked values are dayjs objects, which moment can't read (it falls back to today),
  // so convert them to a Date first.
  const formattedDate = selectedDate
    ? moment(selectedDate?.toDate ? selectedDate.toDate() : selectedDate).format("DD MMM, YYYY")
    : "";

  const handleOpenModal = () => {
    if (disabled) {
      onClick?.();
      return;
    }
    handleLogEvent();
    setDate(undefined);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  return (
    <>
      <div className={`${styles.datePicketBox} DatePicketBox`} ref={anchorRef}>
        <div
          onClick={isModal ? handleOpenModal : handleToggle}
          className={`${styles.datePickerContainer} DatePickerContainer ${
            formattedDate ? "withDateStyleWrapper" : ""
          } ${toggle || openModal ? "activeWrapper" : ""}`}
        >
          <Box
            className={` ${styles.dateLabel} dateLabel`}
            id={`${id}_calenderIcon`}
          >
            {isCalendericon && (
              <CalendarIconNew className={`${styles.dateIcon} dateIcon`} />
            )}
            <ParagraphText
              className={`fontSize12 barclayBlue ${error ? "foundationRedNormalColor" : ""}`}
            >
              {resolvedTitle}
            </ParagraphText>
          </Box>
          <Box
            tabIndex={0}
            className={`${styles.dateRangeInput} dateRangeInput ${toggle ? styles.active : ""} ${
              formattedDate ? "withDateStyle" : ""
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                return isModal ? handleOpenModal() : handleToggle();
              }
            }}
          >
            {formattedDate ? (
              formattedDate
            ) : placeholder ? (
              <ParagraphText
                className={`${placeholderColor ? placeholderColor : "newLightColor"} `}
              >
                {placeholder}
              </ParagraphText>
            ) : (
              ""
            )}
          </Box>
        </div>
        <ParagraphText className="inputError">{error || ""}</ParagraphText>

        {/* Modal for isModal = true */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="date-picker-modal"
          aria-describedby="date-picker-modal-description"
        >
          <Box className={styles.modalContent}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className={`${styles.datePicketBox} DatePicketBox`}>
                <div
                  className={`${styles.datePickerWrapper} ${styles.dateSelectWrapper} ${className} datePickerWrapper`}
                  id="DatePicketBoxId"
                >
                  <ParagraphText className={styles.pickerTitle}>
                    {resolvedTitle}
                  </ParagraphText>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                    <StaticDatePicker
                      slots={{ layout: CustomLayout }}
                      defaultValue={dayjs(selectedDate)}
                      onChange={handleChange}
                      leftArrow
                      dayOfWeekFormatter={(week) => week}
                      PaperProps={{
                        sx: {
                          svg: { color: "white" },
                          span: { color: "white" },
                        },
                      }}
                      disablePast={disablePast}
                      disableFuture={disableFuture}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </LocalizationProvider>
                  <Box
                    display={"flex"}
                    gap={1.2}
                    padding={2}
                    justifyContent={"end"}
                  >
                    {!isRequired && (formattedDate || date) && (
                      <UBButton
                        className="clearDateBtn"
                        onClick={onClearDate}
                        title={I18n.clear}
                        id={`${id}_clear`}
                      />
                    )}
                    <UBButton
                      className="applyDateBtn"
                      onClick={onApplyDate}
                      title={I18n.apply}
                      id={`${id}_apply`}
                      disabled={!date && !selectedDate}
                    />
                  </Box>
                </div>
              </div>
            </ClickAwayListener>
          </Box>
        </Modal>

        {/* Dropdown date picker - use Popover when !isModal (renders in portal, positions below input, flips up when no space) */}
        {!isModal && (
          <Popover
            open={toggle}
            anchorEl={anchorRef.current}
            onClose={() => setToggle(false)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              className: `${styles.datePickerWrapper} ${styles.dropdownPopover} ${className}`,
              sx: { zIndex: 1400, overflow: "visible" },
            }}
            disableScrollLock
          >
            <ClickAwayListener onClickAway={handleClickAway}>
              <div
                id="DatePicketBoxId"
                className={`${className} ${styles.dropdownContent} ${!closeOnDateSelect ? styles.dropdownContentWithActions : ""}`}
              >
                {resolvedTitle && !closeOnDateSelect && (
                  <ParagraphText className={styles.pickerTitle}>
                    {resolvedTitle}
                  </ParagraphText>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                  <StaticDatePicker
                    slots={{ layout: CustomLayout }}
                    defaultValue={dayjs(selectedDate)}
                    onChange={handleChange}
                    leftArrow
                    disableFuture={disableFuture}
                    dayOfWeekFormatter={(week) => week}
                    PaperProps={{
                      sx: {
                        svg: { color: "var(--foundationBlueDark)" },
                        span: { color: "var(--foundationBlueDark)" },
                      },
                    }}
                    disablePast={disablePast}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </LocalizationProvider>
                {!closeOnDateSelect && (
                  <Box
                    className={styles.dropdownFooter}
                    display="flex"
                    gap={1.2}
                    padding="16px 20px 20px"
                    justifyContent="flex-end"
                  >
                    {!isRequired && (formattedDate || date) && (
                      <UBButton
                        className="clearDateBtn"
                        onClick={onClearDate}
                        title={I18n.clear}
                        id={`${id}_clear`}
                      />
                    )}
                    <UBButton
                      className="applyDateBtn"
                      onClick={onApplyDate}
                      id={`${id}_apply`}
                      title={I18n.apply}
                      disabled={!date && !selectedDate}
                    />
                  </Box>
                )}
              </div>
            </ClickAwayListener>
          </Popover>
        )}
      </div>
    </>
  );
};

export default UBDateSelect;
